import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AnalyticsService } from '@common/services/analytics.service';
import { CommerceService } from '@common/services/commerce.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ACTIVATION_FEE_USD, CreditAccessStatus, CreditosService, LATE_GRACE_CALENDAR_DAYS } from '../../services/creditos.service';
import { crBannerHiddenKey, hideBanner, isBannerHidden } from '../../utils/banner-hide.util';
import { CreditEventOnce, trackCreditEvent } from '../../utils/credit-analytics.util';

/**
 * Banner que muestra el estado de la próxima cuota / mora del crédito vigente
 * del tenant. Visible a TODOS los usuarios (no solo al owner).
 *
 * Diseño:
 *   - T-3..T-1: amarillo, "Faltan X días para que se venza tu cuota..."
 *   - T-0: rojo, "Tu cuota del préstamo vence hoy..."
 *   - T+1..T+3 (en gracia): rojo, "Vencio hace X días. En Y días bloqueo + fee $15"
 *   - Bloqueado (T+4+): el modal global se encarga, este banner muestra el resumen.
 *
 * Se autosuscribe al estado y dispara `refreshAccessStatus$` al inicializar.
 * Se puede embeber en home/dashboard y en el módulo créditos.
 */
@Component({
    selector: 'fina-credit-due-banner',
    templateUrl: './credit-due-banner.component.html',
    styleUrls: ['./credit-due-banner.component.scss'],
})
export class CreditDueBannerComponent implements OnInit, OnDestroy {
    status: CreditAccessStatus | null = null;
    /** X para ocultarlo (Home, FINA-3378): dura la sesión y vale por variante; otra variante vuelve a mostrarse. */
    @Input() dismissible = false;
    private destroy$ = new Subject<void>();
    /** `cr_due_banner_viewed` sale una vez por variante mientras vive el banner (FINA-3412). */
    private readonly creditEvents = new CreditEventOnce();

    constructor(public creditosService: CreditosService, private commerceService: CommerceService, private analyticsService: AnalyticsService) {}

    /** Tasa BCV USD del día — para el "(Bs. X)" inline junto al fee (oculto si no cargó). */
    get vesRate$() {
        return this.commerceService.exchangeRateBCV$;
    }

    ngOnInit(): void {
        this.creditosService.accessStatus$.pipe(takeUntil(this.destroy$)).subscribe(s => {
            this.status = s;
            if (this.visible && this.creditEvents.first(this.variant)) trackCreditEvent(this.analyticsService, 'dueBannerViewed', this.dueBannerProps());
        });
        // Side-effect: traer el estado actualizado al montar.
        this.creditosService.refreshAccessStatus$().subscribe({ error: () => {} });
    }

    /** Props del banner: `placement` sale de `dismissible` (solo el Home lo deja ocultar). */
    private dueBannerProps(): Record<string, unknown> {
        return {
            variant: this.variant,
            placement: this.dismissible ? 'home' : 'module',
            days_overdue: this.diasVencidos,
            days_to_due: this.status?.daysToNextDue ?? null,
            pending_verification: this.pendingVerification,
        };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Mostrar/ocultar: solo si hay crédito activo y la cuota está dentro de [-3, +3]. Bloqueo (≥+4) lo cubre el modal. */
    get visible(): boolean {
        return CreditosService.isDueBannerVisible(this.status) && !this.dismissed;
    }

    get dismissed(): boolean {
        return this.dismissible && isBannerHidden(crBannerHiddenKey('due', this.variant));
    }

    hide(): void {
        hideBanner(crBannerHiddenKey('due', this.variant));
        trackCreditEvent(this.analyticsService, 'dueBannerDismissed', this.dueBannerProps());
    }

    /** Variante visual — define color/iconografía. */
    get variant(): 'pre' | 'today' | 'overdue' | 'blocked' {
        const s = this.status!;
        if (s.blocked) return 'blocked';
        if (s.daysOverdue >= 1) return 'overdue';
        if (s.daysToNextDue === 0) return 'today';
        return 'pre';
    }

    /** Días para el copy según corresponda. */
    get diasFaltantes(): number {
        return this.status?.daysToNextDue ?? 0;
    }

    get diasVencidos(): number {
        return this.status?.daysOverdue ?? 0;
    }

    /**
     * Días de gracia que restan ANTES del bloqueo del día 4 de mora.
     * Política: 3 días de gracia (T+1, T+2, T+3) → al T+4 se activa el bloqueo.
     *   - T+1 (overdue=1) → quedan 2 días de gracia (T+2 y T+3) → "En 2 días bloqueo"
     *   - T+2 (overdue=2) → queda 1 día de gracia (T+3) → "En 1 día bloqueo"
     *   - T+3 (overdue=3) → 0 días → último día; copy especial "Mañana bloqueo" en el template
     */
    get diasParaBloqueo(): number {
        return Math.max(0, LATE_GRACE_CALENDAR_DAYS - this.diasVencidos);
    }

    /** True cuando el usuario está en el último día de gracia (T+3). El template usa otro copy. */
    get esUltimoDiaGracia(): boolean {
        return this.diasVencidos === LATE_GRACE_CALENDAR_DAYS;
    }

    /** Fee a mostrar: el que informa el backend cuando está bloqueado (puede ser menor si ya se abonó parte); si no, el nominal. */
    get activationFee(): number {
        const s = this.status;
        return s?.blocked && typeof s.activationFee === 'number' && s.activationFee > 0 ? s.activationFee : ACTIVATION_FEE_USD;
    }

    /** Abonos: la cuota vencida ya está cubierta y solo falta la verificación de soporte. */
    get pendingVerification(): boolean {
        return !!this.status?.pendingVerification;
    }

    /** Todo lo que hay que pagar para desbloquear (fee incluido), según el backend. */
    get totalToUnblock(): number | null {
        return this.status?.totalToUnblock ?? null;
    }
}
